import { SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import { Input, Layout } from 'antd';
import React, { ReactNode } from 'react';
import { BiChevronRight } from 'react-icons/bi';
import { useLocation } from 'react-router';
import { ReactComponent as Mail } from '../assets/svgIcons/mail.svg';
import Account from '../components/Account/Account';
import GlobalSearch from '../components/global-search/global-search-modal';
import { useGlobalSearch } from '../components/global-search/useGlobalSearch';
import { useActivate2fa } from '../hooks/use-activate-2fa';
import { useAuthorizedRoles } from '../pages/two-fa/data';
import { headerStyles } from './styles';
import { useAuth } from '../hooks/use-auth';
import { useRoleGetter } from '../helpers/getUserInfo';
import {
  admin,
  kancelar,
  pjesmarres,
  specialistFv,
} from '../components/common/common-functions/common-functions';

const { Header, Content } = Layout;

interface SidebarLayoutProps {
  collapsed: boolean;
  children: ReactNode;
  isMobile?: boolean;
}

const SidebarLayout = ({
  children,
  collapsed,
  isMobile = false,
}: SidebarLayoutProps) => {
  const { pathname } = useLocation();
  const { data: loggedInUser } = useAuth();
  const { activateTfa } = useActivate2fa();
  const {
    isSearchOpen,
    searchQuery,
    setSearchQuery,
    openSearch,
    closeSearch,
    clearSearch,
  } = useGlobalSearch();
  const role = useRoleGetter();
  const showSearch = [admin, specialistFv, pjesmarres, kancelar].includes(role);

  const tfaPathnames = ['/two-fa'].includes(pathname);
  const showBanner =
    useAuthorizedRoles() && !tfaPathnames && !loggedInUser?.is2FAEnabled;

  const handleClearHeaderSearch = (e: React.MouseEvent) => {
    e.stopPropagation();
    clearSearch();
  };

  return (
    <Layout
      className="site-layout"
      style={{
        marginLeft: isMobile ? 0 : collapsed ? 80 : 200,
      }}
    >
      <div
        className={`fixed top-0 z-[100] ${
          isMobile
            ? 'left-0 right-0'
            : collapsed
              ? 'right-0 left-20'
              : 'right-0 left-[200px]'
        }`}
      >
        <div
          className="flex items-center justify-end gap-2 px-4 text-white"
          style={{
            backgroundColor: '#003666',
            height: '32px',
            fontSize: isMobile ? '11px' : '14px',
          }}
        >
          <Mail className={isMobile ? 'w-3 h-3' : 'w-4 h-4'} />
          <a className="truncate">smvshm-support@magjistratura.edu.al</a>
        </div>

        <Header
          className="site-layout-background flex flex-row items-center gap-2"
          style={{
            ...headerStyles,
            height: isMobile ? '56px' : '64px',
            padding: isMobile ? '16px 12px' : '0 24px',
          }}
        >
          {!isMobile && showSearch && (
            <div
              className="flex items-center"
              style={{ width: '300px', flexShrink: 0 }}
            >
              <Input
                placeholder={searchQuery || 'Kërko...'}
                value={searchQuery}
                prefix={<SearchOutlined style={{ color: '#999' }} />}
                suffix={
                  searchQuery ? (
                    <CloseCircleFilled
                      style={{
                        color: '#999',
                        fontSize: '14px',
                        cursor: 'pointer',
                      }}
                      onClick={handleClearHeaderSearch}
                    />
                  ) : (
                    !isMobile && (
                      <span
                        style={{
                          fontSize: '11px',
                          color: '#999',
                          backgroundColor: '#f0f0f0',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontWeight: 500,
                        }}
                      >
                        Ctrl+K
                      </span>
                    )
                  )
                }
                readOnly
                onClick={openSearch}
                style={{
                  cursor: 'pointer',
                  backgroundColor: '#fafafa',
                  borderRadius: '8px',
                  width: '100%',
                }}
              />
            </div>
          )}

          {showBanner && !isMobile && (
            <div className="flex flex-1 items-center justify-center gap-1 px-2 text-center lg:gap-2">
              <span className="text-xs font-medium text-yellow-700 lg:text-sm">
                Për sigurinë tuaj, ju sugjerojmë të aktivizoni autentikimin me
                dy faktorë (2FA).
              </span>
              <BiChevronRight
                size={20}
                className="cursor-pointer lg:size-6"
                onClick={activateTfa}
              />
            </div>
          )}

          <div className="flex flex-row flex-shrink-0 ml-auto">
            <Account isMobile={isMobile} />
          </div>
        </Header>
      </div>

      <Content
        style={{
          overflow: 'initial',
          background: tfaPathnames ? '' : 'white',
          marginTop: isMobile ? '88px' : '96px',
          marginBottom: isMobile ? '64px' : '0',
          minHeight: isMobile ? 'calc(100vh - 152px)' : 'calc(100vh - 96px)',
        }}
      >
        <div
          className="site-layout-background"
          style={{ padding: isMobile ? 16 : 24 }}
        >
          {children}
        </div>
      </Content>

      <GlobalSearch
        visible={isSearchOpen}
        onClose={closeSearch}
        loggedInUser={loggedInUser}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
      />
    </Layout>
  );
};

export default SidebarLayout;
